import React, { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Loader2, Sparkles, Mic, Square } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Floating concierge chat bubble — bottom-right, open to anyone.
 * Claude Haiku 4.5 with tool use (availability, quotes, tentative bookings),
 * streamed over SSE so Daisy's answer starts appearing almost instantly.
 * Voice: tap the mic and talk — Whisper transcribes, Daisy answers out loud
 * (OpenAI TTS) while the text appears on screen. */
const ConciergeChat = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hi, I'm Daisy — the Walnut Lakes concierge, named after our little Daisy Lake. Ask me anything, and when you're ready I can hold a booking for you and Justina will call to confirm." },
  ]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [checking, setChecking] = useState(false);
  const [recording, setRecording] = useState(false);
  const [voiceBusy, setVoiceBusy] = useState(false);
  const sessionRef = useRef(null);
  const scrollerRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);

  // Restore session id
  useEffect(() => {
    let sid = localStorage.getItem("wlx_chat_session");
    if (!sid) {
      sid = `s-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      localStorage.setItem("wlx_chat_session", sid);
    }
    sessionRef.current = sid;
  }, []);

  useEffect(() => {
    if (open && scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [messages, open, checking, recording]);

  const patchLast = (patch) =>
    setMessages((m) => {
      const copy = [...m];
      copy[copy.length - 1] = { ...copy[copy.length - 1], ...patch };
      return copy;
    });

  const send = async () => {
    const text = input.trim();
    if (!text || sending || voiceBusy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }]);
    setSending(true);
    setChecking(false);
    // Empty assistant bubble — tokens stream into it as Daisy writes.
    setMessages((m) => [...m, { role: "assistant", content: "", streaming: true }]);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionRef.current, message: text }),
      });
      if (!res.ok || !res.body) throw new Error("chat failed");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const frames = buf.split("\n\n");
        buf = frames.pop() || "";
        for (const frame of frames) {
          const line = frame.trim();
          if (!line.startsWith("data:")) continue;
          let evt;
          try {
            evt = JSON.parse(line.slice(5).trim());
          } catch {
            continue;
          }
          if (evt.delta) {
            setChecking(false);
            setMessages((m) => {
              const copy = [...m];
              const lastMsg = copy[copy.length - 1];
              copy[copy.length - 1] = { ...lastMsg, content: (lastMsg.content || "") + evt.delta };
              return copy;
            });
          } else if (evt.status === "checking") {
            setChecking(true);
          } else if (evt.error) {
            throw new Error(evt.error);
          }
        }
      }
      patchLast({ streaming: false });
      // If the stream ended with nothing, fall back gracefully
      setMessages((m) => {
        const copy = [...m];
        const lastMsg = copy[copy.length - 1];
        if (lastMsg?.role === "assistant" && !lastMsg.content) {
          copy[copy.length - 1] = { role: "assistant", content: "Sorry — I hit a small hiccup. Please try again or email justina@walnutlakes.co.uk." };
        }
        return copy;
      });
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Sorry — I hit a small hiccup. Please try again or email justina@walnutlakes.co.uk." };
        return copy;
      });
    } finally {
      setSending(false);
      setChecking(false);
    }
  };

  // ---- Voice -------------------------------------------------------------
  const startRecording = async () => {
    if (recording || voiceBusy || sending) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const rec = new MediaRecorder(stream);
      recorderRef.current = rec;
      chunksRef.current = [];
      rec.ondataavailable = (e) => {
        if (e.data?.size) chunksRef.current.push(e.data);
      };
      rec.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
        if (blob.size > 500) {
          sendVoice(blob);
        } else {
          toast.error("Too short — hold a thought and tap the mic again.");
        }
      };
      rec.start();
      setRecording(true);
    } catch {
      toast.error("Microphone access is needed for voice — check your browser permissions, or just type below.");
    }
  };

  const stopRecording = () => {
    if (!recording) return;
    setRecording(false);
    recorderRef.current?.stop();
  };

  const sendVoice = async (blob) => {
    setVoiceBusy(true);
    // Placeholder bubble while Whisper + Daisy + TTS do their thing
    setMessages((m) => [...m, { role: "assistant", content: "", streaming: true }]);
    try {
      const fd = new FormData();
      fd.append("session_id", sessionRef.current);
      fd.append("file", blob, "voice.webm");
      const res = await fetch(`${API}/chat/voice`, { method: "POST", body: fd });
      const data = await res.json().catch(() => null);
      if (!res.ok || !data) {
        throw new Error(data?.detail || "I couldn't hear that — please try again.");
      }
      // Replace the placeholder with the transcript + Daisy's answer
      setMessages((m) => {
        const copy = [...m];
        copy.pop();
        return [...copy, { role: "user", content: `🎤 ${data.transcript}` }, { role: "assistant", content: data.reply }];
      });
      if (data.audio_b64) {
        try {
          const audio = new Audio(`data:audio/mpeg;base64,${data.audio_b64}`);
          await audio.play();
        } catch { /* autoplay blocked — text is on screen anyway */ }
      }
    } catch (e) {
      const msg = e?.message || "Sorry — I hit a small hiccup. Please try again.";
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: msg };
        return copy;
      });
    } finally {
      setVoiceBusy(false);
    }
  };
  // ------------------------------------------------------------------------

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      {/* Floating bubble — raised on phones/tablets so it never covers the
          gallery booking tile (or other bottom bars); desktop stays put. */}
      <button
        onClick={() => setOpen((v) => !v)}
        className={`fixed z-[999] bottom-24 right-5 md:bottom-28 md:right-8 lg:bottom-8 w-14 h-14 rounded-full shadow-2xl flex items-center justify-center transition-all duration-200 hover:scale-105 ${
          open ? "bg-[#0F0E0E] text-[#F5EFE6]" : "bg-gradient-to-br from-[#B76E79] to-[#8C4E58] text-white"
        }`}
        aria-label={open ? "Close chat" : "Open concierge chat"}
        data-testid="concierge-bubble"
      >
        {open ? <X size={22} /> : <MessageCircle size={22} />}
        {!open && (
          <span className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-[#3B8C4E] ring-2 ring-white animate-pulse" />
        )}
      </button>

      {/* Chat panel */}
      {open && (
        <div className="fixed z-[998] bottom-40 right-4 md:bottom-44 md:right-8 lg:bottom-28 w-[calc(100vw-2rem)] max-w-md h-[600px] max-h-[calc(100vh-10rem)] bg-[#F5EFE6] rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-[#DDD1BE]" data-testid="concierge-panel">
          <div className="bg-[#0F0E0E] text-[#F5EFE6] px-5 py-4 flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#B76E79] to-[#8C4E58] flex items-center justify-center">
              <Sparkles size={16} />
            </div>
            <div className="flex-1">
              <p className="font-serif text-lg leading-none">Daisy</p>
              <p className="text-[10px] uppercase tracking-[0.18em] text-[#B76E79] mt-1">Walnut Lakes concierge</p>
            </div>
            <span className="text-[9px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full bg-[#3B8C4E] text-white">Online</span>
          </div>

          <div ref={scrollerRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="concierge-messages">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                  m.role === "user"
                    ? "bg-gradient-to-br from-[#B76E79] to-[#8C4E58] text-white rounded-br-sm"
                    : "bg-white text-[#14100F] border border-[#DDD1BE] rounded-bl-sm"
                }`}>
                  {m.content}
                  {m.streaming && !m.content && (
                    <span className="inline-flex items-center gap-2 text-[#B76E79]" data-testid="concierge-typing">
                      <Loader2 size={14} className="animate-spin" />
                      {checking
                        ? <span className="text-[11px] text-[#6A5F58] italic" data-testid="concierge-checking">Checking live availability…</span>
                        : voiceBusy
                          ? <span className="text-[11px] text-[#6A5F58] italic" data-testid="concierge-voice-thinking">Listening &amp; thinking…</span>
                          : null}
                    </span>
                  )}
                  {m.streaming && m.content && (
                    <span className="inline-block w-1.5 h-3.5 bg-[#B76E79] align-middle ml-0.5 animate-pulse" />
                  )}
                </div>
              </div>
            ))}
            {recording && (
              <div className="flex justify-end">
                <div className="bg-gradient-to-br from-[#B76E79] to-[#8C4E58] text-white rounded-2xl rounded-br-sm px-4 py-2.5 text-sm flex items-center gap-2" data-testid="concierge-recording">
                  <span className="w-2 h-2 rounded-full bg-white animate-pulse" /> Listening… tap the mic to send
                </div>
              </div>
            )}
          </div>

          <div className="p-3 border-t border-[#DDD1BE] bg-white">
            <div className="flex gap-2">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKey}
                placeholder="Ask about dates, dogs, fishing, prices…"
                rows={1}
                className="flex-1 resize-none rounded-full border border-[#DDD1BE] px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#B76E79]"
                data-testid="concierge-input"
              />
              <button
                onClick={recording ? stopRecording : startRecording}
                disabled={sending || voiceBusy}
                className={`w-10 h-10 rounded-full flex items-center justify-center transition-all disabled:opacity-40 ${
                  recording
                    ? "bg-[#8C2E39] text-white animate-pulse"
                    : "bg-white border border-[#DDD1BE] text-[#B76E79] hover:bg-[#F5EFE6]"
                }`}
                aria-label={recording ? "Stop and send voice message" : "Talk to Daisy"}
                title={recording ? "Stop and send" : "Talk to Daisy"}
                data-testid="concierge-mic"
              >
                {voiceBusy ? <Loader2 size={14} className="animate-spin" /> : recording ? <Square size={13} /> : <Mic size={15} />}
              </button>
              <button
                onClick={send}
                disabled={sending || voiceBusy || !input.trim()}
                className="w-10 h-10 rounded-full bg-gradient-to-br from-[#B76E79] to-[#8C4E58] text-white flex items-center justify-center disabled:opacity-40 hover:scale-105 transition-transform"
                data-testid="concierge-send"
              >
                {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              </button>
            </div>
            <p className="text-[10px] text-[#6A5F58] mt-2 text-center">
              Type or tap the mic and talk — Daisy can check live availability, quote, and hold a booking.
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default ConciergeChat;
